(function () {
  /* Hero wordmark → header logo handoff.
     Scroll-scrubbed: position and scale are computed from scroll position on
     every frame, so the lockup tracks the wheel/finger exactly, reverses
     cleanly and never relies on timers. The header lockup is sized from the
     hero lockup's real proportions, so the closing cross-fade lands on an
     exact scaled copy instead of a near-miss.
     Writes custom properties only; the CSS lives in the "Logo handoff v2"
     block at the end of co-studio.css. */
  function setupWordmark(wordmark) {
    if (wordmark.dataset.coWordmarkReady === 'true') return;
    const home = wordmark.closest('.co-home');
    const origin = home && home.querySelector('[data-co-wordmark-origin]');
    const nav = home && home.querySelector('.co-nav');
    const hero = home && home.querySelector('.co-hero');
    const heroC = wordmark.querySelector('.co-word--creative');
    const heroO = wordmark.querySelector('.co-word--objekt');
    const headC = home && home.querySelector('.co-nav__text-brand-creative');
    const headO = home && home.querySelector('.co-nav__text-brand-objekt');
    if (!home || !origin || !nav || !hero || !heroC || !heroO || !headC || !headO) return;
    wordmark.dataset.coWordmarkReady = 'true';
    wordmark.classList.remove('is-docking', 'is-docked', 'is-reduced', 'is-scroll-linked');
    wordmark.classList.add('is-ready');
    home.classList.add('co-logo-v2', 'is-wordmark-docked');

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const FADE_FROM = 0.84;   // progress where the cross-fade into the header mark starts
    let geo = null;
    let frame = 0;

    const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
    const lerp = (a, b, t) => a + (b - a) * t;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const smooth = (a, b, t) => { const x = clamp((t - a) / (b - a), 0, 1); return x * x * (3 - 2 * x); };

    function measure() {
      // Natural (untransformed) geometry of the hero lockup, relative to the wordmark box.
      const scaleNow = parseFloat(wordmark.style.getPropertyValue('--co-logo-scale')) || 1;
      const wm = wordmark.getBoundingClientRect();
      const c = heroC.getBoundingClientRect();
      const o = heroO.getBoundingClientRect();
      const unit = (v) => v / scaleNow;
      const lock = {
        width: wordmark.offsetWidth || 1,
        oLeft: unit(o.left - wm.left), oWidth: unit(o.width) || 1,
        cTop: unit(c.top - wm.top), oTop: unit(o.top - wm.top), cWidth: unit(c.width), cHeight: unit(c.height)
      };

      // Make the header lockup an exact scaled copy of the hero lockup.
      const headObjektWidth = headO.getBoundingClientRect().width || 98;
      const k = headObjektWidth / lock.oWidth;
      headC.style.setProperty('width', `${(lock.cWidth * k).toFixed(2)}px`, 'important');
      headO.style.setProperty('margin-top', `${((lock.oTop - lock.cTop - lock.cHeight) * k).toFixed(2)}px`, 'important');

      const navBox = nav.getBoundingClientRect();
      const ho = headO.getBoundingClientRect();
      const hc = headC.getBoundingClientRect();
      const originBox = origin.getBoundingClientRect();
      geo = {
        startX: originBox.left - lock.width / 2,
        startY: originBox.top + window.scrollY,   // document y of the lockup's top edge at rest
        endX: ho.left - lock.oLeft * k,
        endY: hc.top - navBox.top - lock.cTop * k,
        scale: k,
        navBottom: navBox.height,
        travel: clamp(lock.width * 0.75, 220, 380)
      };
      render();
    }

    function render() {
      frame = 0;
      if (!geo) return;
      const scrollY = window.scrollY;
      const p = clamp(scrollY / geo.travel, 0, 1);
      let x, y, scale, fade;
      if (reducedMotion.matches) {
        // No travel: the mark scrolls away with the hero and the header mark simply appears.
        x = geo.startX; y = geo.startY - scrollY; scale = 1; fade = p >= 0.5 ? 1 : 0;
      } else {
        const ex = easeOut(p);
        x = lerp(geo.startX, geo.endX, ex);
        scale = lerp(1, geo.scale, ex);
        // Follow the page up, but stay below the header bar until the mark is
        // small and over the logo slot; only then lift into the bar. It lands
        // exactly as the cross-fade begins, so the two marks never ghost.
        const natural = lerp(geo.startY - scrollY, geo.endY, easeInOut(p));
        const floor = lerp(geo.navBottom + 10, geo.endY, smooth(0.5, FADE_FROM, p));
        y = Math.max(natural, floor);
        fade = clamp((p - FADE_FROM) / (1 - FADE_FROM), 0, 1);
      }
      wordmark.style.setProperty('--co-logo-x', `${x.toFixed(2)}px`);
      wordmark.style.setProperty('--co-logo-y', `${y.toFixed(2)}px`);
      wordmark.style.setProperty('--co-logo-scale', scale.toFixed(4));
      wordmark.style.setProperty('--co-logo-hero-opacity', (1 - fade).toFixed(3));
      home.style.setProperty('--co-logo-header-opacity', fade.toFixed(3));
      wordmark.classList.toggle('is-handed-off', fade >= 1);
    }

    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(render); };
    let resizeFrame = 0;
    const remeasure = () => { if (!resizeFrame) resizeFrame = window.requestAnimationFrame(() => { resizeFrame = 0; measure(); }); };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', remeasure);
    window.addEventListener('orientationchange', remeasure);
    window.addEventListener('load', remeasure);
    if (window.ResizeObserver) new ResizeObserver(remeasure).observe(hero);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasure);
    wordmark.querySelectorAll('img').forEach((img) => { if (!img.complete) img.addEventListener('load', remeasure, { once: true }); });
    reducedMotion.addEventListener && reducedMotion.addEventListener('change', render);
    measure();
  }

  function init(root) { root.querySelectorAll('[data-co-wordmark]').forEach(setupWordmark); }

  function setupCapabilityExperience() {
    const panels = document.querySelectorAll('[data-co-capability-panel]');
    if (!panels.length) return;
    const targetId = decodeURIComponent(window.location.hash.slice(1));
    panels.forEach((panel) => {
      if (targetId && panel.id === targetId) panel.classList.add('is-visible');
    });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      panels.forEach((panel) => panel.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, instance) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        instance.unobserve(entry.target);
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
    panels.forEach((panel) => observer.observe(panel));
  }

  // Department pages use their own "dpage" parameter: Shopify reserves "page" for its server-side
  // pagination, so ?page=2 used to request an empty second page of the collection.
  function setupDepartmentFilters() {
    const grid = document.querySelector('#product-grid[data-id]');
    const links = document.querySelectorAll('[data-department-filter]');
    if (!grid || !links.length) return;
    const department = new URLSearchParams(window.location.search).get('department') || 'all';
    const requestedPage = Math.max(1, Number(new URLSearchParams(window.location.search).get('dpage')) || 1);
    const pageSize = 16;
    const items = [...grid.querySelectorAll(':scope > li[data-departments]')];
    const matchingItems = items.filter((item) => {
      const departments = item.dataset.departments.split(/\s+/);
      return department === 'all' || departments.includes(department);
    });
    const totalPages = Math.max(1, Math.ceil(matchingItems.length / pageSize));
    const currentPage = Math.min(requestedPage, totalPages);
    if (requestedPage !== currentPage) {
      const canonicalUrl = new URL(window.location.href);
      if (currentPage === 1) canonicalUrl.searchParams.delete('dpage');
      else canonicalUrl.searchParams.set('dpage', currentPage);
      window.history.replaceState({}, '', `${canonicalUrl.pathname}${canonicalUrl.search}${canonicalUrl.hash}`);
    }
    const pageStart = (currentPage - 1) * pageSize;
    const pageItems = new Set(matchingItems.slice(pageStart, pageStart + pageSize));
    items.forEach((item) => { item.hidden = !pageItems.has(item); });
    links.forEach((link) => {
      const active = link.dataset.departmentFilter === department;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
    const pagination = document.querySelector('[data-department-pagination]');
    if (pagination) {
      pagination.replaceChildren();
      pagination.hidden = totalPages <= 1;
      if (totalPages > 1) {
        const makeLink = (label, page, current) => {
          const link = document.createElement('a');
          const target = new URL(window.location.href);
          if (page <= 1) target.searchParams.delete('dpage');
          else target.searchParams.set('dpage', page);
          if (department === 'all') target.searchParams.delete('department');
          else target.searchParams.set('department', department);
          link.href = `${target.pathname}${target.search}${target.hash}`;
          link.textContent = label;
          link.className = 'co-department-pagination__link';
          if (current) link.setAttribute('aria-current', 'page');
          return link;
        };
        if (currentPage > 1) pagination.append(makeLink('Previous', currentPage - 1, false));
        for (let page = 1; page <= totalPages; page += 1) {
          pagination.append(makeLink(String(page), page, page === currentPage));
        }
        if (currentPage < totalPages) pagination.append(makeLink('Next', currentPage + 1, false));
      }
    }
    // Department links always point at the full catalogue (/collections/all). When the shopper is
    // already there, they also keep any active sort/filters; only the department (and page) change.
    const current = new URL(window.location.href);
    links.forEach((link) => {
      if (!link.dataset.baseHref) link.dataset.baseHref = link.getAttribute('href');
      const base = new URL(link.dataset.baseHref, current.origin);
      const next = base.pathname === current.pathname ? new URL(current.href) : base;
      next.searchParams.delete('dpage');
      const target = link.dataset.departmentFilter;
      if (target === 'all') next.searchParams.delete('department');
      else next.searchParams.set('department', target);
      link.setAttribute('href', `${next.pathname}${next.search}`);
    });
    // The product count reflects the department, not the whole catalogue.
    if (department !== 'all') {
      const label = `${matchingItems.length} ${matchingItems.length === 1 ? 'product' : 'products'}`;
      ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
        const count = document.getElementById(id);
        if (count) count.textContent = label;
      });
      document.querySelectorAll('.mobile-facets__count').forEach((count) => { count.textContent = label; });
    }
    const empty = document.querySelector('[data-department-empty]');
    if (empty) empty.hidden = matchingItems.length > 0;
  }

  function setupCommerceOverlays() {
    const search = document.querySelector('[data-co-search-overlay]');
    const searchInput = search && search.querySelector('input[type="search"]');
    const openSearch = () => {
      if (!search) return;
      search.hidden = false;
      search.setAttribute('aria-hidden', 'false');
      document.body.classList.add('co-search-open');
      window.requestAnimationFrame(() => searchInput && searchInput.focus());
    };
    const closeSearch = () => {
      if (!search) return;
      search.hidden = true;
      search.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('co-search-open');
    };
    document.querySelectorAll('[data-co-search-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => { event.preventDefault(); openSearch(); });
    });
    search && search.querySelectorAll('[data-co-search-close]').forEach((button) => button.addEventListener('click', closeSearch));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && search && !search.hidden) closeSearch(); });

    document.querySelectorAll('[data-co-cart-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') { event.preventDefault(); drawer.open(trigger); }
      });
    });
    document.querySelectorAll('[data-co-add-to-cart]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        if (!button || button.disabled) return;
        const originalLabel = button.textContent;
        button.disabled = true;
        button.textContent = 'Adding';
        try {
          const response = await fetch(`${form.action}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(form),
          });
          if (!response.ok) throw new Error('Unable to add this item');
          const item = await response.json();
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') {
            const sections = await fetch('/?sections=cart-drawer,cart-icon-bubble', { headers: { Accept: 'application/json' } }).then((result) => result.json());
            drawer.renderContents({ sections, id: item.id });
            refreshCartCount();
          } else {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          }
          button.textContent = 'Added';
          window.setTimeout(() => { button.textContent = originalLabel; button.disabled = false; }, 1200);
        } catch (error) {
          button.textContent = 'Try again';
          window.setTimeout(() => { button.textContent = originalLabel; button.disabled = false; }, 1600);
        }
      });
    });
    const refreshCartCount = () => fetch('/cart.js').then((response) => response.json()).then((cart) => {
      document.querySelectorAll('[data-co-cart-count]').forEach((count) => { count.textContent = String(cart.item_count).padStart(2, '0'); });
    }).catch(() => {});
    refreshCartCount();
    ['cart:refresh', 'cart:updated', 'product-form:added'].forEach((eventName) => document.addEventListener(eventName, refreshCartCount));
    // Dawn's product form, cart page and drawer announce changes through pubsub rather than DOM events.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCartCount);

    // Mobile menus: close on link tap, outside tap or Escape.
    const mobileMenus = document.querySelectorAll('.co-global-nav__mobile, .co-nav__mobile');
    const closeMenus = () => mobileMenus.forEach((menu) => menu.removeAttribute('open'));
    mobileMenus.forEach((menu) => menu.addEventListener('click', (event) => { if (event.target.closest('a')) closeMenus(); }));
    document.addEventListener('click', (event) => { if (!event.target.closest('.co-global-nav__mobile, .co-nav__mobile')) closeMenus(); });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeMenus(); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setupCommerceOverlays);
  else setupCommerceOverlays();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setupDepartmentFilters);
  else setupDepartmentFilters();
  // Re-apply after Shopify's filter/sort re-renders the grid without a page load.
  document.addEventListener('co:collection-rendered', setupDepartmentFilters);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setupCapabilityExperience);
  else setupCapabilityExperience();
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
